import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BarChart2, Users, DollarSign, ShieldAlert, Building, Award, TrendingUp, RefreshCw, Loader, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, color = "text-white", testId }) {
  return (
    <div className="bg-[#1A2035] border border-white/8 rounded-xl p-4 dark-section" data-testid={testId}>
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#161B28] border border-white/8 rounded-2xl overflow-hidden dark-section">
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-brand-green" />
          <h2 className="text-sm font-bold text-white">{title}</h2>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

export default function MarketplaceDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [health, revenue, supply, trust, facilities, organizers, plus] = await Promise.all([
        fetch(`${API}/admin/dashboard/market-health`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/revenue`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/supply-demand`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/trust-safety`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/facilities`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/organizers`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/dashboard/plus-subscriptions`, { credentials: "include" }).then(r => r.json()),
      ]);
      setData({ health, revenue, supply, trust, facilities, organizers, plus });
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section">
        <div className="text-center">
          <p className="text-red-400 text-lg font-bold mb-2">Admin Access Required</p>
          <Link to="/" className="text-brand-green hover:underline text-sm">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const h = data.health || {};
  const r = data.revenue || {};
  const s = data.supply || {};
  const t = data.trust || {};
  const p = data.plus || {};

  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="marketplace-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-black text-white">Marketplace Operations</h1>
            <p className="text-gray-500 text-sm mt-0.5">{lastRefresh ? `Last refreshed: ${lastRefresh}` : "Loading data…"}</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-xl hover:bg-white/10 transition-colors text-sm disabled:opacity-50"
            aria-label="Refresh dashboard"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {loading && !data.health ? (
          <div className="flex justify-center py-20"><Loader size={32} className="text-brand-green animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Market Health */}
            <Section title="Market Health" icon={TrendingUp}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-1">
                <StatCard label="Active Users" value={h.active_users} testId="stat-active-users" />
                <StatCard label="Available This Week" value={h.available_this_week} color="text-brand-green" testId="stat-available" />
                <StatCard label="Confirmed" value={h.sessions?.confirmed} testId="stat-confirmed" />
                <StatCard label="Completed" value={h.sessions?.completed} color="text-brand-green" testId="stat-completed" />
                <StatCard label="Completion Rate" value={h.completion_rate != null ? `${h.completion_rate}%` : "—"} testId="stat-completion-rate" />
                <StatCard label="No-show Rate" value={h.no_show_rate != null ? `${h.no_show_rate}%` : "—"} color={h.no_show_rate > 10 ? "text-red-400" : "text-white"} testId="stat-noshow-rate" />
              </div>
            </Section>

            {/* Revenue */}
            <Section title="Revenue" icon={DollarSign}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
                <StatCard label="Gross Revenue" value={`$${r.gross_revenue ?? 0}`} color="text-brand-green" testId="stat-gross-revenue" />
                <StatCard label="Net Revenue" value={`$${r.net_revenue ?? 0}`} testId="stat-net-revenue" />
                <StatCard label="Refunds" value={`$${r.refunds ?? 0}`} color={r.refunds > 0 ? "text-red-400" : "text-white"} testId="stat-refunds" />
                <StatCard label="ARPU" value={`$${r.arpu ?? 0}`} sub={`${r.active_users} active users`} testId="stat-arpu" />
              </div>
              {r.revenue_by_type && Object.keys(r.revenue_by_type).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Revenue by Type</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(r.revenue_by_type).map(([k, v]) => (
                      <span key={k} className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-gray-300">
                        {k}: <strong className="text-white">${v}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Plus Subscriptions */}
            <Section title="Plus Subscriptions" icon={Award}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
                <StatCard label="Total Subscribers" value={p.total_subscribers} color="text-brand-green" testId="stat-subscribers" />
                <StatCard label="Pro" value={p.pro} testId="stat-pro" />
                <StatCard label="Elite" value={p.elite} testId="stat-elite" />
                <StatCard label="Cancelled" value={p.cancelled} color={p.cancelled > 0 ? "text-yellow-400" : "text-white"} testId="stat-cancelled" />
              </div>
              {p.cancellation_reasons?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Cancellation Reasons</p>
                  <div className="flex flex-wrap gap-2">
                    {p.cancellation_reasons.map((c) => (
                      <span key={c.reason} className="text-xs px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                        {c.reason}: {c.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Supply/Demand */}
            <Section title="Supply & Demand" icon={BarChart2} defaultOpen={false}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
                <StatCard label="Users w/o Matches" value={s.users_no_matches} color={s.users_no_matches > 50 ? "text-red-400" : "text-white"} testId="stat-no-matches" />
                <StatCard label="Available Gap" value={s.availability ? `${s.availability.gap_pct}%` : "—"} testId="stat-availability-gap" />
                <StatCard label="Waitlisted" value={s.total_waitlisted} testId="stat-waitlisted" />
              </div>
              {s.skill_distribution?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Skill Distribution</p>
                  <div className="flex flex-wrap gap-2">
                    {s.skill_distribution.map((d) => (
                      <span key={d.level} className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-gray-300 capitalize">
                        {d.level}: <strong className="text-white">{d.count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Trust/Safety */}
            <Section title="Trust & Safety" icon={ShieldAlert} defaultOpen={false}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
                <StatCard label="Total Reports" value={t.total_reports} testId="stat-reports" />
                <StatCard label="Unresolved" value={t.unresolved_reports} color={t.unresolved_reports > 0 ? "text-red-400" : "text-white"} testId="stat-unresolved" />
                <StatCard label="Total Blocks" value={t.total_blocks} testId="stat-blocks" />
                <StatCard label="Repeat Offenders" value={t.repeat_offenders} color={t.repeat_offenders > 0 ? "text-red-400" : "text-white"} testId="stat-offenders" />
              </div>
            </Section>

            {/* Facilities */}
            <Section title="Facility Partners" icon={Building} defaultOpen={false}>
              {data.facilities?.facilities?.length === 0 ? (
                <p className="text-gray-500 text-sm py-3">No facility partners yet.</p>
              ) : (
                <div className="overflow-x-auto mt-1">
                  <table className="w-full text-sm" aria-label="Facility performance">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-white/8">
                        <th className="pb-2 pr-4">Facility</th>
                        <th className="pb-2 pr-4">City</th>
                        <th className="pb-2 pr-4">Views</th>
                        <th className="pb-2 pr-4">Requests</th>
                        <th className="pb-2">Confirmed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.facilities?.facilities || []).map((f) => (
                        <tr key={f.id} className="border-b border-white/4">
                          <td className="py-2 pr-4 text-white font-medium">{f.name}</td>
                          <td className="py-2 pr-4 text-gray-400">{f.city}</td>
                          <td className="py-2 pr-4 text-gray-300">{f.views}</td>
                          <td className="py-2 pr-4 text-gray-300">{f.session_requests}</td>
                          <td className="py-2 text-brand-green">{f.sessions_confirmed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* Organizers */}
            <Section title="Organizer Performance" icon={Users} defaultOpen={false}>
              {data.organizers?.organizers?.length === 0 ? (
                <p className="text-gray-500 text-sm py-3">No approved organizers yet.</p>
              ) : (
                <div className="overflow-x-auto mt-1">
                  <table className="w-full text-sm" aria-label="Organizer performance">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-white/8">
                        <th className="pb-2 pr-4">Organizer</th>
                        <th className="pb-2 pr-4">City</th>
                        <th className="pb-2 pr-4">Sessions</th>
                        <th className="pb-2 pr-4">Completed</th>
                        <th className="pb-2 pr-4">Attendees</th>
                        <th className="pb-2">Fill Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.organizers?.organizers || []).map((o) => (
                        <tr key={o.id} className="border-b border-white/4">
                          <td className="py-2 pr-4 text-white font-medium">{o.name}</td>
                          <td className="py-2 pr-4 text-gray-400">{o.city}</td>
                          <td className="py-2 pr-4 text-gray-300">{o.sessions_created}</td>
                          <td className="py-2 pr-4 text-brand-green">{o.sessions_completed}</td>
                          <td className="py-2 pr-4 text-gray-300">{o.total_attendees}</td>
                          <td className="py-2 text-gray-300">{o.fill_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
